import {
  DeferredCodeBlock as CodeBlock,
  frameworkLabels,
  getRepositoryCustomTypes,
  invalidateDocumentListData
} from "@prismicio/editor-fields"
import { localStorage } from "@prismicio/editor-support/DOM"
import { useOnChange } from "@prismicio/editor-support/React"
import { invalidateFetcherData } from "@prismicio/editor-support/Suspense"
import {
  Box,
  Button,
  Card,
  CardContent,
  CardImage,
  Separator,
  Text,
  useToast
} from "@prismicio/editor-ui"
import { useQueryClient } from "@tanstack/react-query"
import { type ReactNode, useState, useSyncExternalStore } from "react"
import { Link } from "react-router-dom"
import { z } from "zod"

import {
  onboarding,
  onboardingSelectedProject,
  onboardingStep
} from "../../app/constants"
import { getBaseRepositoryUrl, getRepositoryName } from "../../app/routing"
import { useRepositoryTypes } from "../../app/services/customTypes"
import { useRepository } from "../../app/services/repository"

export function Onboarding() {
  const { currentStep } = useOnboarding()

  const Component = stepMap[currentStep]

  if (!Component) return null

  return <Component />
}

const stepMap = {
  welcome: WelcomeStep,
  chooseMainLanguage: PlaceholderStep,
  launchStarter: LaunchStarterStep,
  pushModels: PushModelsStep,
  createProject: CreateProjectStep,
  setupSliceMachine: SetupSliceMachineStep,
  runSliceMachine: RunSliceMachineStep
} as const

const starterSteps = ["welcome", "launchStarter"] as const
const starterStepsSchema = z.enum(starterSteps).catch("welcome")

const nonStarterSteps = [
  "welcome",
  "chooseMainLanguage",
  "createProject",
  "setupSliceMachine",
  "runSliceMachine",
  "pushModels"
] as const
const nonStarterStepsSchema = z.enum(nonStarterSteps).catch("welcome")

function useOnboarding() {
  const { starterId } = useRepository()

  const steps = starterId ? starterSteps : nonStarterSteps
  const stepSchema = starterId ? starterStepsSchema : nonStarterStepsSchema

  const currentStep = useSyncExternalStore(localStorage.subscribe, () =>
    stepSchema.parse(localStorage.read(onboardingStep))
  )

  function goToNextStep() {
    const nextStepIndex = steps.findIndex((step) => step === currentStep) + 1
    const nextStep = steps[nextStepIndex]
    if (nextStep) localStorage.write(onboardingStep, nextStep)
  }

  function goToPreviousStep() {
    const previousStepIndex =
      steps.findIndex((step) => step === currentStep) - 1
    const previousStep = steps[previousStepIndex]
    if (previousStep) localStorage.write(onboardingStep, previousStep)
  }

  return {
    currentStep,
    steps,
    goToNextStep,
    goToPreviousStep
  }
}

function useRevalidateCustomTypes() {
  const [isRevalidating, setIsRevalidating] = useState(false)

  const queryClient = useQueryClient()
  const { showToast } = useToast()

  const customTypes = useRepositoryTypes()

  async function revalidate() {
    setIsRevalidating(true)

    try {
      await invalidateDocumentListData(queryClient)
      invalidateFetcherData(getRepositoryCustomTypes)
    } finally {
      setIsRevalidating(false)
    }
  }

  // updated cache data isn't immediately available during the revalidate procedure
  // thus we await the changes and notify the user based on that
  useOnChange(customTypes, () => {
    // TODO: This should be handled within the try...catch of the revalidate procedure
    // move this when switching to react-query, as we should be able to read the
    // cache custom types value properly after update
    if (customTypes.length === 0) {
      showToast({
        title: "It looks like you haven’t pushed your models yet",
        icon: "alert"
      })
    } else {
      showToast({
        title: "You pushed your models successfully"
      })
    }
  })

  return {
    revalidate,
    isRevalidating
  }
}

interface StepProps {
  image: string
  children: ReactNode
}

function Step(props: StepProps) {
  const { children, image } = props

  return (
    <Card
      padding={0}
      sx={{ width: 498, marginTop: 80, marginInline: "auto" }}
      variant="outlined"
    >
      <CardImage src={image} height={280} />

      <CardContent padding={32} gap={0}>
        {children}
      </CardContent>
    </Card>
  )
}

function WelcomeStep() {
  const { goToNextStep } = useOnboarding()

  return (
    <Step image={onboarding.welcome}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Welcome to your Page Builder
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 24 }}>
        This is where you'll create and edit pages. But first you'll have to
        define the models for your pages with Slice Machine.
      </Text>

      <Button endIcon="arrowForward" size="large" onClick={goToNextStep}>
        Get started
      </Button>
    </Step>
  )
}

function LaunchStarterStep() {
  const { revalidate, isRevalidating } = useRevalidateCustomTypes()

  const { goToPreviousStep } = useOnboarding()

  const { starterId } = useRepository()
  const repositoryName = getRepositoryName()

  const starterRepo = (starterId && starterRepositories[starterId]) ?? ""

  return (
    <Step image={onboarding.launchStarter}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Launch your starter
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Your starter has everything you need to get started, including Prismic’s
        development tool, Slice Machine. Run this command to download and
        install your starter:
      </Text>

      <CodeBlock
        code={`npx @slicemachine/init@latest --repository ${repositoryName} --starter ${starterRepo}`}
        language="bash"
        title="Terminal"
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button
          color="grey"
          size="large"
          onClick={goToPreviousStep}
          disabled={isRevalidating}
        >
          Back
        </Button>

        <Button
          endIcon="arrowForward"
          size="large"
          onClick={revalidate}
          loading={isRevalidating}
        >
          I'm done
        </Button>
      </Box>
    </Step>
  )
}

function PushModelsStep() {
  const { revalidate, isRevalidating } = useRevalidateCustomTypes()
  const { goToPreviousStep } = useOnboarding()

  return (
    <Step image={onboarding.pushModels}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Push your models from Slice Machine
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Create one or more Custom Types in{" "}
        <a
          href="http://localhost:9999"
          target="_blank"
          style={{ textDecoration: "none" }}
          rel="noreferrer"
        >
          Slice Machine
        </a>{" "}
        and push them to Prismic. Then come back here and continue.
      </Text>

      <Button
        invisible
        asChild
        startIcon="arrowForward"
        sx={{
          alignSelf: "start"
        }}
      >
        <a
          href="https://prismic.io/docs/content-modeling"
          target="_blank"
          rel="noreferrer"
        >
          Learn more about creating content models.
        </a>
      </Button>

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button
          color="grey"
          size="large"
          onClick={goToPreviousStep}
          disabled={isRevalidating}
        >
          Back
        </Button>

        <Button
          endIcon="arrowForward"
          size="large"
          onClick={revalidate}
          loading={isRevalidating}
        >
          I’ve pushed my models
        </Button>
      </Box>
    </Step>
  )
}

function RunSliceMachineStep() {
  const { goToPreviousStep, goToNextStep } = useOnboarding()

  return (
    <Step image={onboarding.runSliceMachine}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Run Slice Machine
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Enter this command to run Slice Machine locally. Once it’s running, open
        it at{" "}
        <a
          href="http://localhost:9999"
          target="_blank"
          style={{ textDecoration: "none" }}
          rel="noreferrer"
        >
          localhost:9999
        </a>
        .
      </Text>

      <CodeBlock code="npm run slicemachine" language="bash" title="Terminal" />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button color="grey" size="large" onClick={goToPreviousStep}>
          Back
        </Button>

        <Button endIcon="arrowForward" size="large" onClick={goToNextStep}>
          Continue
        </Button>
      </Box>
    </Step>
  )
}

function SetupSliceMachineStep() {
  const { goToPreviousStep, goToNextStep } = useOnboarding()
  const repositoryName = getRepositoryName()

  // The stored project is persisted from the previous step "Create project"
  // If the user has followed the guide with our code snippets, their local project
  // will have the stored name, if they haven't we use the fallback
  const storedProject = localStorage.read(onboardingSelectedProject)
  const projectName = storedProject || "<your project name>"

  return (
    <Step image={onboarding.launchStarter}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Set up Slice Machine
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Slice Machine is the Prismic tool for creating models. Install it in
        your Next.js project by running these commands:
      </Text>

      <CodeBlock
        code={`cd ${projectName}
npx @slicemachine/init@latest --repository ${repositoryName}`}
        language="bash"
        title="Terminal"
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button color="grey" size="large" onClick={goToPreviousStep}>
          Back
        </Button>

        <Button endIcon="arrowForward" size="large" onClick={goToNextStep}>
          Continue
        </Button>
      </Box>
    </Step>
  )
}

function CreateProjectStep() {
  const { goToNextStep, goToPreviousStep } = useOnboarding()

  const { framework } = useRepository()
  const repositoryName = getRepositoryName()

  // if the user has selected a unsupported framework we allow them to go straight to the custom types builder
  if (
    framework !== "next" &&
    framework !== "nuxt" &&
    framework !== "sveltekit"
  ) {
    return <UnsupportedFrameworkStep />
  }

  const label = frameworkLabels[framework]
  const createProjectCodeSnippet = frameworkCreateProjectCodeSnippet[framework]

  function goToNextStepAndSelectProject(project: string) {
    goToNextStep()
    localStorage.write(onboardingSelectedProject, project)
  }

  return (
    <Step image={onboarding.createProject}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Create a {label} project
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 16 }}>
        Before we get started, you need a {label} project to work with. Open
        your terminal and enter this command:
      </Text>

      <CodeBlock
        code={`${createProjectCodeSnippet} ${repositoryName}`}
        language="bash"
      />

      <Separator style="dashed" sx={{ marginBlock: 24 }} />

      <Box justifyContent="space-between">
        <Button color="grey" size="large" onClick={goToPreviousStep}>
          Back
        </Button>

        <Box gap={16} alignItems="center">
          <Button
            invisible
            color="grey"
            onClick={() => {
              goToNextStepAndSelectProject("")
            }}
          >
            I already have a project
          </Button>
          <Button
            endIcon="arrowForward"
            size="large"
            onClick={() => {
              goToNextStepAndSelectProject(repositoryName)
            }}
          >
            Continue
          </Button>
        </Box>
      </Box>
    </Step>
  )
}

function UnsupportedFrameworkStep() {
  const baseRepoUrl = getBaseRepositoryUrl()
  const customTypeBuilderUrl = `${baseRepoUrl}/masks`

  return (
    <Step image={onboarding.createProject}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Build your first custom type
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 24 }}>
        Custom types allow you to define and configure fields for your content.
        Define custom types for pages, posts, events, authors, products, places,
        ...
      </Text>

      <Button size="large" asChild>
        <Link to={customTypeBuilderUrl}>Create custom type</Link>
      </Button>
    </Step>
  )
}

function PlaceholderStep() {
  const { goToNextStep } = useOnboarding()

  return (
    <Step image={onboarding.welcome}>
      <Text variant="h3" sx={{ marginBottom: 8 }}>
        Placeholder
      </Text>

      <Text variant="normal" color="grey11" sx={{ marginBottom: 24 }}>
        This is a placeholder to simulate the onboarding behavior.
      </Text>

      <Button endIcon="arrowForward" size="large" onClick={goToNextStep}>
        Continue
      </Button>
    </Step>
  )
}

// this reference points at the starter repositories users can choose between
// when setting up their initial Prismic repository
const starterRepositories: Record<string, string> = {
  next_minimal: "nextjs-starter-prismic-minimal",
  next_multi_page: "nextjs-starter-prismic-multi-page",
  nuxt_minimal: "nuxt-starter-prismic-minimal",
  nuxt_multi_page: "nuxt-starter-prismic-multi-page",
  sveltekit_minimal: "sveltekit-starter-prismic-minimal",
  sveltekit_multi_page: "sveltekit-starter-prismic-multi-page"
}

const frameworkCreateProjectCodeSnippet = {
  next: "npx create-next-app@latest",
  nuxt: "npx nuxi@latest init",
  sveltekit: "npm create svelte@latest"
}
